import { FAQ } from '@/app/components/faq/FAQ';
import { StickyButton } from '@/app/components/ppra/components/ppra-results-page/components';
import HowItWorks from '@/app/components/ppra/components/ppra-results-page/components/how-it-works/HowItWorks';
import animationData from '@/assets/animations/risk-graph.json';
import { HeroVideo } from '@/components/hero-video/hero-video';
import MarkdownText from '@/components/markdown/markdown-text';
import NavigationButton from '@/components/navigation/navigation-button';
import { OptimizedImage } from '@/components/optimized-image';
import { ROUTE_PAYMENT } from '@/constants/routes';
import useStickyButton from '@/hooks/use-sticky-button';
import { Box, ButtonProps, Flex, Image, Section, Text } from '@cmpsr/components';
import Lottie from 'lottie-react';
import { DemoAndIndustryProofs } from '../demo-and-industry-proofs/demo-and-industry-proofs';
import { PersonalizedList } from '../personalized-list/personalized-list';
import { SocialProofMarquee } from '../social-proof-marquee/social-proof-marquee';

function HeroWomanHeadlineSection({
  ctaText,
  ctaButtonStyle,
  mainButtonRef,
  headline,
  yearlyPrice,
}: {
  ctaText: string;
  ctaButtonStyle: ButtonProps;
  mainButtonRef: React.RefObject<HTMLButtonElement>;
  headline?: string | null;
  yearlyPrice?: string;
}) {
  const priceText = yearlyPrice ?? '...';
  const defaultHeadline = `500 risk factors, 21 cancers, **mapped to you for ${priceText}**`;
  const displayHeadline = headline || defaultHeadline;

  return (
    <Section height={{ base: '20rem', md: '22rem', lg: '24.6875rem', xl: '35rem' }} position="relative" width="100%">
      <HeroVideo
        src="/videos/catch-hero.mp4"
        children={
          <Box maxWidth={{ base: '100%', lg: '90%' }} display="flex" flexDirection="column" gap="2rem" alignItems="center">
            <MarkdownText color="#FFF" fontSize={{ base: '1.875rem', lg: '4.54688rem' }} lineHeight={'111%'}>
              {displayHeadline}
            </MarkdownText>
            <NavigationButton to={ROUTE_PAYMENT} ref={mainButtonRef} {...ctaButtonStyle}>
              {ctaText}
            </NavigationButton>
          </Box>
        }
      />
    </Section>
  );
}

function CancerInterestMessageSection({
  cancerInterestMessage,
}: {
  cancerInterestMessage?: string;
}) {
  return (
    <Section display="flex" flexDirection="column" gap="2rem" alignItems="center" maxWidth={{ base: '95%', lg: '90%' }}>
      <MarkdownText
        fontSize={{ base: '1.875rem', lg: '4.23463rem' }}
        lineHeight="111%"
        textAlign="center"
        flexDirection="column"
      >
        {cancerInterestMessage}
      </MarkdownText>
    </Section>
  );
}

function PersonalizedSection({
  personalizedMessages,
  cancerInterestMessage,
}: {
  personalizedMessages?: string[];
  cancerInterestMessage?: string | null;
}) {
  return (
    <Box
      display="flex"
      justifyContent="center"
      gap={{ base: '2rem', lg: '4rem' }}
      flexDirection="column"
      alignItems="center"
    >
      <Section maxWidth={{ base: '90%', lg: '720px' }}>
        <PersonalizedList personalizedMessages={personalizedMessages} />
      </Section>
      {cancerInterestMessage && (
        <Section display="flex" justifyContent="center" alignItems="center">
          <CancerInterestMessageSection
            cancerInterestMessage={cancerInterestMessage}
          />
        </Section>
      )}
    </Box>
  );
}

function HowItWorksSection() {
  return (
    <Section display="flex" flexDirection="column" gap="2rem" alignItems="center" width="90%">
      <HowItWorks />
    </Section>
  );
}

function LottieRiskHeadlineSection() {
  return (
    <Section
      display="flex"
      flexDirection={{ base: 'column', lg: 'row' }}
      gap="2rem"
      alignItems="center"
      paddingX={{ base: 'spacer-2', lg: 'none' }}
      maxWidth={{ base: '100%', lg: '90%' }}
    >
      <MarkdownText
        fontSize={{ base: '1.875rem', lg: '4.375rem' }}
        lineHeight="111%"
        textAlign="center"
        maxW={{ base: '100%', lg: '550px', xl: '750px' }}
      >
        Catch members can reduce their cancer risk **by up to 60%**
      </MarkdownText>
      <Box maxWidth={{ base: '700px', lg: '800px', xl: '500px' }} maxHeight={{ base: '400px', lg: '300px' }}>
        <Lottie animationData={animationData} />
      </Box>
    </Section>
  );
}

function CausesInfographicsSection() {
  const imageSizes = [
    { width: 300, descriptor: '300w' },
    { width: 400, descriptor: '400w' },
    { width: 600, descriptor: '600w' },
    { width: 800, descriptor: '800w' },
    { width: 1200, descriptor: '1200w' },
  ];

  const generateSrcSet = (imageName: string, format: string) => {
    return imageSizes
      .map(({ width, descriptor }) =>
        `/images/info-images/optimized/${imageName}-${width}w.${format} ${descriptor}`
      )
      .join(', ');
  };

  return (
    <Section
      display="flex"
      flexDirection="column"
      gap="2rem"
      alignItems="center"
      paddingX={{ base: 'spacer-2', lg: 'none' }}
      maxWidth={{ base: '90%', lg: '1000px' }}
    >
      <MarkdownText fontSize={{ base: '1.875rem', lg: '4.375rem' }} lineHeight="111%" textAlign="center">
        It's time for a new paradigm in cancer: **Prevention**
      </MarkdownText>
      <Flex
        flexDirection={{ base: 'column', lg: 'row' }}
        justifyContent="center"
        width="100%"
        alignItems="center"
        gap={{ base: '2rem', lg: '3rem' }}
      >
        <Box
          display={{ base: 'none', md: 'flex' }}
          alignItems="center"
          justifyContent="center"
          width="342px"
          height="294px"
          position="relative"
          ml={{md: "30px", lg: "0px"}}
        >
          <OptimizedImage
            src="/images/info-images/optimized/cancer-cause-misconceptions-400w.png"
            srcSet={generateSrcSet('cancer-cause-misconceptions', 'png')}
            sizes="312px"
            alt="Misconceptions about cancer causes graph"
            priority={true}
            objectFit="contain"
            aspectRatio={400 / 377}
            width="100%"
            height="100%"
            skeletonProps={{
              borderRadius: 'lg',
              bg: 'gray.200',
            }}
            sources={[
              {
                type: 'image/avif',
                srcSet: generateSrcSet('cancer-cause-misconceptions', 'avif'),
              },
            ]}
          />
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={{ base: '100%', md: '384px' }}
          height={{ base: 'auto', md: '263px' }}
          maxWidth={{ base: '384px', md: '384px' }}
          position="relative"
          mt={{md: "24px"}}
          ml={{base: "10px", lg: "0px"}}
        >
          <OptimizedImage
            src="/images/info-images/optimized/cancer-causes-400w.png"
            srcSet={generateSrcSet('cancer-causes', 'png')}
            sizes="384px"
            alt="Cancer causes graph"
            priority={true}
            objectFit="contain"
            aspectRatio={400 / 274}
            width="100%"
            height="100%"
            skeletonProps={{
              borderRadius: 'lg',
              bg: 'gray.200',
            }}
            sources={[
              {
                type: 'image/avif',
                srcSet: generateSrcSet('cancer-causes', 'avif'),
              },
            ]}
          />
        </Box>
      </Flex>
    </Section>
  );
}

function SocialProofsSection() {
  return (
    <Section display="flex" flexDirection="column" gap="3rem" alignItems="center" width="100%">
      <MarkdownText
        fontSize={{ base: '1.875rem', lg: '4.375rem' }}
        lineHeight="111%"
        textAlign="center"
        width="90%"
        maxWidth="1000px"
      >
        Join thousands of members **reducing their cancer risk**
      </MarkdownText>
      <SocialProofMarquee />
    </Section>
  );
}

function FAQSection() {
  return (
    <Section display="flex" flexDirection="column" gap="2rem" alignItems="center" width="90%">
      <Text fontSize={{ base: '2.23463rem', md: '4.23463rem' }} lineHeight="111%" maxW="800px" textAlign="center">
        Frequently Asked Questions
      </Text>
      <FAQ variant="dark" />
    </Section>
  );
}

const ctaButtonStyle: ButtonProps = {
  display: 'flex',
  justifyContent: 'center',
  borderRadius: 'radii-button',
  background: 'salmon-light',
  color: 'dark-blue',
  textTransform: 'capitalize',
  fontSize: { base: '1.25rem', lg: '1.25rem' },
  lineHeight: '130%',
  fontWeight: 700,
  paddingX: '2.5rem',
  paddingY: '0.75rem',
  _hover: {
    background: '#ff794f',
  },
  width: '20rem',
};

interface QuizletSellScreenProps {
  headline?: string | null;
  personalizedMessages?: string[];
  cancerInterestMessage?: string | null;
  ctaText: string;
  yearlyPrice?: string;
}

export default function QuizletSellScreen({
  headline,
  personalizedMessages,
  cancerInterestMessage,
  ctaText,
  yearlyPrice,
}: QuizletSellScreenProps) {
  const { showStickyButton, mainButtonRef } = useStickyButton();
  const ctaLink = ROUTE_PAYMENT;

  return (
    <Flex
      backgroundColor="white"
      width="100%"
      height="100%"
      flexDirection="column"
      gap={{ base: '3rem', md: '5rem', lg: '6rem' }}
      alignItems="center"
      pb="8rem"
    >
      <Flex flexDirection="column" gap={{ base: '3rem', md: '5rem' }} width="100%" alignItems="center">
        <HeroWomanHeadlineSection
          ctaText={ctaText}
          ctaButtonStyle={ctaButtonStyle}
          mainButtonRef={mainButtonRef}
          headline={headline}
          yearlyPrice={yearlyPrice}
        />
        <PersonalizedSection
          personalizedMessages={personalizedMessages}
          cancerInterestMessage={cancerInterestMessage}
        />
        <DemoAndIndustryProofs />
        <HowItWorksSection />
        <LottieRiskHeadlineSection />
      </Flex>
      <CausesInfographicsSection />
      <SocialProofsSection />
      <FAQSection />
      {showStickyButton && <StickyButton ctaText={ctaText} ctaLink={ctaLink} ctaButtonStyle={ctaButtonStyle} />}
    </Flex>
  );
}
